import {MdbsCore} from "@/api/tsclient";
import {dataFieldsEditModule, studyDefinitionModule, studyPatientContext} from "@/store";
import DataFieldCache from "@/models/data.field.cache";

import ConstraintDefinitionDto = MdbsCore.ConstraintDefinitionDto;
import DataFieldShortDto = MdbsCore.DataFieldShortDto;
import DataFieldDefinitionDto = MdbsCore.DataFieldDefinitionDto;
import {dateComparer} from "@/auxillary/dateComparer";
import {isBranchingFieldType} from "@/auxillary/datafield.cache.helpers";
import InstrumentContextCache from "@/models/instrument.context.cache";
import {eventBus} from "@/main";
import VisitContextCache from "@/models/visit.context.cache";
import {DateTime} from "luxon";

function resolveSimpleStandaloneInstrumentFieldValue(
    constraintDefinitionType: string, instrumentIdentifier: string, fields: Array<string>,
    operator: string, value: string, testValue: any) {
    const instrument = studyPatientContext.standaloneInstruments.find(i => i.identifier == instrumentIdentifier)
    if (instrument === undefined)
        return true;

    return resolveSimpleInstrumentFieldValue(constraintDefinitionType, instrument, fields, operator, value, testValue)
}

function resolveSimpleVisitInstrumentFieldValue(
    constraintDefinitionType: string, visitIdentifier: string, instrumentIdentifier: string, fields: Array<string>,
    operator: string, value: string, testValue: any) {
    const visitDef = studyDefinitionModule.studyDefinition.visits
        ?.find(v => v.identifier == visitIdentifier && !v.isDeleted)
    if (visitDef?.instruments === undefined) {
        console.warn("cannot find visit instrument definitions: " + visitIdentifier);
        return true;
    }

    const visit = studyPatientContext.visits?.find(v => v.definition.id == visitDef.id)
    if (visit === undefined)
        return true;

    const instrument = visit.instruments.find(i => i.identifier == instrumentIdentifier)
    if (instrument === undefined)
        return true;

    return resolveSimpleInstrumentFieldValue(constraintDefinitionType, instrument, fields, operator, value, testValue)
}

function resolveSimpleInstrumentFieldValue(constraintDefinitionType: string,
                                           instrument: Readonly<InstrumentContextCache>,
                                           fields: Array<string>, operator: string, value: string, testValue: any) {
    console.log(operator, value, testValue)
    const fieldDefs = instrument.definition.fields ?? []
    let constraintFieldDefinition: DataFieldDefinitionDto | undefined = undefined;
    let constraintField: DataFieldShortDto | undefined = undefined;
    for (let field of fields) {
        if (constraintFieldDefinition == null) {
            constraintFieldDefinition = fieldDefs
                .find(f => f.identifier == field && !f.isDeleted)
        } else {
            constraintFieldDefinition = fieldDefs
                .find(f => f.identifier == field && f.parentFieldDefinitionId == constraintFieldDefinition?.id && !f.isDeleted)
        }
        if (constraintFieldDefinition === undefined) {
            console.warn("cannot find Field Path in definition ", fields)
            return true;
        }

        if (constraintField === undefined)
            constraintField = instrument.fields.find(d => d.definitionId == constraintFieldDefinition?.id);
        else
            constraintField = constraintField?.childFields?.find(d => d.definitionId == constraintFieldDefinition?.id);
    }
    if (constraintField === undefined) {
        return true;
    }

    if (operator == "isOngoingMedication") {
        const visitId = eventBus.$route?.params.visitId;
        const visit = studyPatientContext.visitById(visitId)
        let children = constraintField.childFields ?? []
        if (visit?.visitDate != null) {
            for (let child of children) {
                let drug = child.childFields?.traverse(f => f.childFields ?? []).find(c => c.definitionId == '68c39a75-1f79-4df1-b3c3-1069d3690706')
                if (drug?.values?.value != value)
                    continue;
                const startDate = child.childFields?.traverse(f => f.childFields ?? []).find(c => c.definitionId == '5f8d60fd-d494-4074-b5c6-4c61ee9b63d4')
                const endDate = child.childFields?.traverse(f => f.childFields ?? []).find(c => c.definitionId == '28d87141-9a69-4325-9534-595c809cf9f2')
                if (!dateComparer("before", startDate?.values?.value, DateTime.fromJSDate(visit.visitDate).toFormat("yyyy-MM-dd"))) {
                    if (!dateComparer("after", endDate?.values?.value, DateTime.fromJSDate(visit.visitDate).toFormat("yyyy-MM-dd"))) {
                        return true;
                    }
                }
            }
        }
        return false;
    }
    return constraintDefinitionType == "SimpleDateValue" ?
        dateComparer(operator, constraintField?.values.value, testValue.value) :
        simpleValueComparator(operator, constraintField?.values?.value, value)
}


function searchFieldInBranch(fields: Array<string>, dataField: DataFieldCache | undefined) {

    if (dataField === undefined)
        return undefined;

    const originDefinition = studyDefinitionModule.dataFieldDefinitionById(dataField.definitionId);
    if (originDefinition === undefined)
        return undefined;

    const referenceId = originDefinition.referenceId
    if (referenceId === undefined)
        return undefined;

    const instrumentDefinition = studyDefinitionModule.instrumentDefinitionById(referenceId)
    if (instrumentDefinition?.fields === undefined)
        return undefined;


    let constraintFieldDefinition: DataFieldDefinitionDto | undefined = undefined;
    for (let field of fields) {
        if (constraintFieldDefinition === undefined) {
            constraintFieldDefinition = instrumentDefinition.fields.find(d => d.identifier == field && !d.isDeleted && !d.definition.isDeleted);
        } else {
            constraintFieldDefinition = instrumentDefinition.fields
                .find(d => d.identifier == field && d.parentFieldDefinitionId == constraintFieldDefinition?.id && !d.isDeleted && !d.definition.isDeleted);
        }
    }

    if (constraintFieldDefinition?.id === undefined)
        return undefined

    let foundField = searchFieldInBranchDown(constraintFieldDefinition.id, dataField)
    if (foundField !== undefined)
        return foundField;

    foundField = searchFieldInBranchUp(constraintFieldDefinition.id, dataField)
    return foundField;
}

function searchFieldInBranchDown(
    fieldDefinitionId: string,
    originField: DataFieldCache, excludeField?: DataFieldCache | undefined)
    : DataFieldCache | undefined {
    if (originField === undefined)
        return undefined;

    if (originField?.childFields === undefined)
        return undefined;

    let foundField = originField.childFields
        .find(f => f.definitionId == fieldDefinitionId && f.id != excludeField?.id);

    if (foundField !== undefined)
        return foundField

    for (let childField of originField.childFields) {
        if (isBranchingField(childField))
            continue;

        foundField = searchFieldInBranchDown(fieldDefinitionId, childField)
        if (foundField !== undefined)
            return foundField;
    }

    return undefined;
}

function searchFieldInBranchUp(
    fieldDefinitionId: string,
    originField: DataFieldCache)
    : DataFieldCache | undefined {
    if (originField === undefined) {
        return undefined;
    }

    const parentField = dataFieldsEditModule.dataFields
        .traverse(c => c.childFields)
        .find(d => d.childFields.some(cd => cd.id == originField.id))

    if (parentField === undefined)
        return undefined;

    if (parentField.definitionId === fieldDefinitionId)
        return parentField; // found it

    let foundField = searchFieldInBranchDown(fieldDefinitionId, parentField, originField)
    if (foundField !== undefined)
        return foundField;

    foundField = searchFieldInBranchUp(fieldDefinitionId, parentField);
    return foundField;
}

function isBranchingField(dataField: DataFieldCache | undefined): boolean {
    return isBranchingFieldType(dataField?.definitionType);
}

function resolveSimpleVisitFieldValue(constraintDefinitionType: string, visitIdentifier: any, fields: any, operator: any,
                                      value: any, testValue: any) {
    const visitDef = studyDefinitionModule.studyDefinition.visits
        ?.find(v => v.identifier == visitIdentifier && !v.isDeleted)
    if (visitDef?.instruments === undefined) {
        console.warn("cannot find visit definitions: " + visitIdentifier);
        return true;
    }

    const visit = studyPatientContext.visits?.find(v => v.definition.id == visitDef.id)
    if (visit === undefined)
        return true;

    const fieldDefs = visit.definition.fields ?? []
    let constraintFieldDefinition: DataFieldDefinitionDto | undefined = undefined;
    let constraintField: DataFieldShortDto | undefined = undefined;
    for (let field of fields) {
        if (constraintFieldDefinition == null) {
            constraintFieldDefinition = fieldDefs
                .find(f => f.identifier == field && !f.isDeleted)
        } else {
            constraintFieldDefinition = fieldDefs
                .find(f => f.identifier == field && f.parentFieldDefinitionId == constraintFieldDefinition?.id && !f.isDeleted)
        }
        if (constraintFieldDefinition === undefined) {
            console.warn("cannot find Field Path in definition ", fields)
            return true;
        }

        if (constraintField === undefined)
            constraintField = visit.fields.find(d => d.definitionId == constraintFieldDefinition?.id);
        else
            constraintField = constraintField?.childFields?.find(d => d.definitionId == constraintFieldDefinition?.id);
    }
    if (constraintField === undefined) {
        return true;
    }

    return constraintDefinitionType == "SimpleDateValue" ?
        dateComparer(operator, constraintField?.values.value, testValue.value) :
        simpleValueComparator(operator, constraintField?.values?.value, value)
}

export const resolveSimpleFieldConstraint = function (constraint: ConstraintDefinitionDto, dataField?: DataFieldCache | undefined) {
    const operator = constraint.definition.operator ?? "equals"
    const value = constraint.definition.value ?? "value"
    const property = constraint.definition.property ?? ""
    const fields = constraint.definition.field ?? []
    const fieldType = constraint.definition.fieldType ?? null
    const visitIdentifier = constraint.definition.visit ?? null
    const instrumentIdentifier = constraint.definition.instrument ?? null

    if (visitIdentifier != null && instrumentIdentifier != null) {
        return resolveSimpleVisitInstrumentFieldValue("SimpleFieldValue",
            visitIdentifier, instrumentIdentifier, fields, operator, value, dataField?.values);
    }

    if (instrumentIdentifier != null) {
        return resolveSimpleStandaloneInstrumentFieldValue("SimpleFieldValue",
            instrumentIdentifier, fields, operator, value, dataField?.values);
    }

    // current data field parent
    if (fieldType == null) {
        let constraintField: DataFieldCache | undefined = undefined;
        let isBranching = false;
        for (let field of fields) {
            if (constraintField === undefined) {
                constraintField = dataFieldsEditModule.dataFields.find(d => d.identifier == field);
            } else {
                constraintField = constraintField.childFields.find(d => d.identifier == field);
            }
            // TODO this is hacky, we need to know it some field is branching based on the definition
            // Is Field branching? (can exist multiple time?)
            if (isBranchingField(constraintField)) {
                isBranching = true;
            }
        }
        if (isBranching) {
            constraintField = searchFieldInBranch(fields, dataField)
        }

        // redirect and hint rules can apply to field itself
        if (constraintField === undefined
            && (dataField?.redirectRule?.constraintExpression?.constraintDefinitionId == constraint.id ||
                dataField?.redirectRule?.constraintExpression?.expressions?.traverse(c =>
                    c.expressions ?? []).some(c => c.constraintDefinitionId == constraint.id) ||
                dataField?.hintRules?.some(hint => hint.constraintExpression?.constraintDefinitionId == constraint.id))) {

            const dataFieldValue = property != "" ? getProperty(dataField?.values, property) : dataField?.values?.value;
            return simpleValueComparator(operator, dataFieldValue, value)
        }

        if (constraintField === undefined) {
            return true;
        }

        return simpleValueComparator(operator, constraintField?.values?.value, value);
    }

    if (fieldType == "patient") {
        if (fields.length == 0)
            return resolvePatientDataValue(constraint);

        const fieldDefinition = studyDefinitionModule.studyDefinition.patient?.fields?.find(f => f.identifier == fields[0] && !f.isDeleted);
        if (fieldDefinition == undefined)
            return false;

        const field = studyPatientContext._studyPatient.fields?.find(f => f.definitionId == fieldDefinition.id);
        const fieldValue = property != "" ? getProperty(field?.values, property) : field?.values?.value;
        return simpleValueComparator(operator, fieldValue, value)
    }

    if (fieldType == "visit") {
        return resolveSimpleVisitFieldValue("SimpleFieldValue",
            visitIdentifier, fields, operator, value, dataField?.values);
    }

    return false;
}

export const resolveSimpleDateConstraint = function (constraint: ConstraintDefinitionDto, dataField?: DataFieldCache | undefined) {
    const operator = constraint.definition.operator ?? "equals"
    const value = constraint.definition.value ?? "value"
    const fields = constraint.definition.field ?? []
    const fieldType = constraint.definition.fieldType ?? null
    const visitIdentifier = constraint.definition.visit ?? null
    const instrumentIdentifier = constraint.definition.instrument ?? null

    if (visitIdentifier != null && instrumentIdentifier != null) {
        return resolveSimpleVisitInstrumentFieldValue("SimpleDateValue", visitIdentifier,
            instrumentIdentifier, fields, operator, value, dataField?.values);
    }

    // current data field parent
    if (fieldType == null) {
        let constraintField: DataFieldCache | undefined = undefined;
        let isBranching = false;
        for (let field of fields) {
            if (constraintField === undefined) {
                constraintField = dataFieldsEditModule.dataFields.find(d => d.identifier == field);
            } else {
                constraintField = constraintField.childFields.find(d => d.identifier == field);
            }
            // TODO this is hacky, we need to know it some field is branching based on the definition
            // Is Field branching? (can exist multiple time?)
            if (isBranchingField(constraintField)) {
                isBranching = true;
            }
        }
        if (isBranching) {
            constraintField = searchFieldInBranch(fields, dataField)
        }

        // redirect and hint rules can apply to field itself
        if (constraintField === undefined
            && (dataField?.redirectRule?.constraintExpression?.constraintDefinitionId == constraint.id
                || dataField?.hintRules?.some(hint => hint.constraintExpression?.constraintDefinitionId == constraint.id))) {
            return dateComparer(operator, undefined, dataField?.values.value);
        }

        if (constraintField === undefined) {
            return true;
        }

        return dateComparer(operator, constraintField?.values.value, dataField?.values.value);
    }

    if (fieldType == "patient") {
        if (fields.length == 0)
            return resolvePatientDataValue(constraint);

        const fieldDefinition = studyDefinitionModule.studyDefinition.patient?.fields?.find(f => f.identifier == fields[0] && !f.isDeleted);
        if (fieldDefinition == undefined)
            return false;
        const field = studyPatientContext._studyPatient.fields?.find(f => f.definitionId == fieldDefinition.id);
        return dateComparer(operator, field?.values?.value, value.value)
    }

    if (fieldType == "visit") {
        console.warn("simple visit constraint not implemented", constraint)
    }

    return false;
}

export const resolvePatientDataValue = function (constraint: ConstraintDefinitionDto) {
    const operator = constraint.definition.operator ?? "equals"
    const value = constraint.definition.value ?? "value"
    let property: string = constraint.definition.property ?? ""
    const patient = studyPatientContext._studyPatient;

    // TODO hacky, just works for mosts studies
    if (property == "birthYear" || property == "sex") {
        const fieldDefinition = studyDefinitionModule.studyDefinition.patient?.fields?.find(f => f.identifier == property && !f.isDeleted);
        if (fieldDefinition == undefined)
            return false;
        const field = patient.fields?.find(f => f.definitionId == fieldDefinition.id);
        return simpleValueComparator(operator, field?.values?.value, value)
    }
    // @ts-ignore
    const propertyValue = getProperty(patient, property);

    return constraint.definitionType == "SimpleDateValue" ?
        dateComparer(operator, propertyValue, value) :
        simpleValueComparator(operator, propertyValue, value)
}

export const resolveVisitDataValue = function (constraint: MdbsCore.ConstraintDefinitionDto, visitCache: VisitContextCache | undefined) {
    const operator = constraint.definition.operator ?? "equals"
    const value = constraint.definition.value ?? "value"
    const visitIdentifier = constraint.definition.visit;
    let property: string = constraint.definition.property ?? ""

    let visit = null;
    if (!visitIdentifier) {
        // TODO Replace hacky solution to get current visit
        const visitId = eventBus.$route?.params.visitId;
        visit = visitCache ?? studyPatientContext._visitContexts.find(v => v.id == visitId);
    } else {
        visit = studyPatientContext._visitContexts.find(v => v.identifier == visitIdentifier);
    }

    if (visit == null)
        return false;

    if (property == "identifier") {
        const propertyValue = getProperty(visit, property)
        return simpleValueComparator(operator, value, propertyValue)
    }

    // state property is not set correctly
    if (property == "state" && value == "new") {
        return operator == "notEquals" ? !visit.isNew : visit.isNew;
    }

    return false;
}

export const resolveInstrumentDataValue = function (constraint: ConstraintDefinitionDto, instrumentCache?: InstrumentContextCache | Readonly<InstrumentContextCache> | undefined) {
    const operator = constraint.definition.operator ?? "equals"
    const value = constraint.definition.value ?? "value"
    let property: string = constraint.definition.property ?? ""

    const instrumentIdentifier = constraint.definition.instrument;
    const visitIdentifier = constraint.definition.visit;

    let instrument = null;
    if (instrumentCache) {
        instrument = instrumentCache
    } else {
        // works if the intermediate exists once, but not for multiple any more
        // visit.identifier not visit.definition.identifier needed then
        instrument = visitIdentifier ?
            studyPatientContext._visitContexts
                .find(v => v.identifier == visitIdentifier && !v.temporaryId)?.instruments.find(i => i.identifier == instrumentIdentifier) :
            studyPatientContext.standaloneInstruments
                .find(i => i.identifier == instrumentIdentifier);
    }
    if (!instrument) {
        const visitId = eventBus.$route?.params.visitId;
        const visit = studyPatientContext._visitContexts.find(v => v.id == visitId);
        instrument = visit?.instruments.find(i => i.identifier == instrumentIdentifier)
    }
    if (!instrument)
        return false;

    // @ts-ignore
    const propertyValue = getProperty(instrument, property);
    return simpleValueComparator(operator, propertyValue, value)
}

function getProperty<T, K extends keyof T>(o: T, propertyName: K): T[K] {
    return o[propertyName]; // o[propertyName] is of type T[K]
}

const simpleValueComparator = function (operator: string, fieldValue: any, testValue: any): boolean {
    if (fieldValue == null && operator == "empty")
        return true;

    if (fieldValue == null)
        return false;

    if (operator == "equals") {
        //console.log(fields, value, operator, constraintField?.values?.value)
        return fieldValue == testValue
    }

    if (operator == "notEquals") {
        return !!fieldValue && fieldValue != testValue
    }

    if (operator == "notEmpty") {
        return fieldValue ? true : false;
    }

    if (operator === "greaterThan") {
        const fieldNumber: number = parseFloat(fieldValue.replace(",", "."));
        return fieldNumber > testValue;
    }

    if (operator === "contains") {
        return fieldValue.includes(testValue);
    }

    console.warn("Unknown operator type", operator)

    return false;
}

export const resolveConstraintExpression = function (
    expression: MdbsCore.ConstraintExpression | undefined,
    dataField?: DataFieldCache | undefined,
    visitCache?: VisitContextCache | undefined,
    instrumentCache?: InstrumentContextCache | Readonly<InstrumentContextCache> | undefined): boolean {
    if (expression === undefined)
        return true;

    // @ts-ignore
    if (expression.operator == "EVAL" || expression.operator == "NOT") {
        const constraint = studyDefinitionModule.studyDefinition.constraints
            ?.find(c => c.id == expression.constraintDefinitionId && !c.isDeleted);
        if (!constraint)
            return false;

        let result = false;
        switch (constraint.definitionType) {
            case "SimpleFieldValue":
                result = resolveSimpleFieldConstraint(constraint, dataField)
                break;
            case "SimpleDateValue":
                result = resolveSimpleDateConstraint(constraint, dataField);
                break;
            case "PatientDataValue":
                result = resolvePatientDataValue(constraint)
                break;
            case "VisitDataValue":
                result = resolveVisitDataValue(constraint, visitCache)
                break;
            case "InstrumentDataValue":
                result = resolveInstrumentDataValue(constraint, instrumentCache)
                break;
            default:
                console.warn("Unknown constraint type", constraint)
        }

        // @ts-ignore
        if (expression.operator == "EVAL") {
            return result
        } else {
            return !result
        }
    }

    // @ts-ignore
    if (expression.operator === "AND") {
        return expression.expressions?.every(c => resolveConstraintExpression(c, dataField, visitCache)) ?? false;
    }

    // @ts-ignore
    if (expression.operator === "OR") {
        return expression.expressions?.some(c => resolveConstraintExpression(c, dataField, visitCache)) ?? false;
    }

    return false
}
